import type { PersistedUser } from "@/entities/user/types/user";
import type { EntityServiceResponseError } from "@/services/blackfisk";
import type { LoggedInUser, SessionAuthResponse, UserAccount } from "@/services/blackfisk/auth";
import type { Ref } from "vue";

import { useLogger } from "@blackfisk/vue/composables/useLogger";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

import { useAuthorizationSync } from "@/composables/useAuthorizationSync";
import { useRequestErrorToast } from "@/composables/useRequestErrorToast";
import { storeDeviceId } from "@/services/blackfisk/deviceId";

const logger = useLogger("client-frontend", "identity-store");
import { displayUserName } from "@/entities/user";
// import { useService } from '@/composables/useService'
import { PermissionControls } from "@/modules/permissions";
import {
  profile,
  sessionLogin,
  sessionLogout,
  sessionQuickLogin,
  quickVerify,
} from "@/services/blackfisk/auth";
import { ensureSession } from "@/services/blackfisk/sessionCoordinator";
import {
  decideEnsureSession,
  decideIdentityResolutionFailure,
  decideRefreshFailure,
  shouldRetryIdentityResolution,
} from "@/services/blackfisk/sessionPolicy";
import { userList } from "@/services/blackfisk/user";

// * Cross-tab adopt window: if a sibling tab broadcast a TOKEN_UPDATE within this window, this
//   tab adopts the broadcast token instead of firing its own refresh (the refresh cookie is
//   single-use/rotating, so only one tab should refresh at a time).
const TOKEN_ADOPT_WINDOW_MS = 15_000;

function clearLegacyToken(): void {
  localStorage.removeItem("token");
}

export const useIdentityStore = defineStore(
  "identity",
  () => {
    const userId = ref(0);
    const userName = ref("");
    const userEmail = ref("");
    const accountId = ref(0);
    const roles: Ref<string[]> = ref([]);
    const accounts: Ref<UserAccount[]> = ref([]);
    const userDictionary = shallowRef<Record<PersistedUser["id"], PersistedUser>>({});
    const accessToken = ref("");
    const csrfToken = ref("");
    const expiresIn = ref(0);
    const isAuthenticated = computed(() => accessToken.value.length > 0);
    const returnUrl: Ref<string> = ref("");
    // * Access-token lifecycle: we track when the current token was issued so we can tell whether
    //   it has expired. There is deliberately no background timer — expiry is checked when the tab
    //   gains focus, since a token only matters while the user is actually looking at the tab.
    let tokenIssuedAt = 0;
    // * Last time a sibling tab broadcast a TOKEN_UPDATE (adopt signal for cross-tab rotation).
    let lastTokenUpdateAt = 0;

    const updateAccountId = (updatedAccountId: number): void => {
      accountId.value = updatedAccountId;
    };
    const activeAccount = computed(() => {
      const accountMap = accounts.value.reduce<Record<string, UserAccount>>(
        (accumulator, account) => {
          accumulator[`${account.id}`] = account;
          return accumulator;
        },
        {},
      );
      // * Assert account always exists in accountMap
      return accountMap[accountId.value]!;
    });
    const userAccountIds = computed(() => accounts.value.map((account) => account.id));

    const { broadcastLogin, broadcastLogout, broadcastTokenUpdate } = useAuthorizationSync();
    const clearTokens = (): void => {
      accessToken.value = "";
      csrfToken.value = "";
    };
    // * In-memory-only token drop: no persisted-state wipe, no LOG_OUT broadcast. Used when the
    //   session cannot be confirmed (transient profile failure after a successful refresh) —
    //   the persisted partial identity (userName + retained accountId) must stay intact and
    //   sibling tabs must be untouched.
    const clearSessionTokens = (): void => {
      clearTokens();
      tokenIssuedAt = 0;
    };
    const noteBroadcastTokenUpdate = (): void => {
      lastTokenUpdateAt = Date.now();
    };
    const hasRecentTokenUpdate = (windowMs = TOKEN_ADOPT_WINDOW_MS): boolean =>
      Date.now() - lastTokenUpdateAt < windowMs;
    const setSessionTokens = (
      sessionAccessToken = "",
      sessionCsrfToken = "",
      sessionExpiresIn = 0,
      issuedAtMs = 0,
      broadcast = true,
    ): void => {
      accessToken.value = sessionAccessToken;
      csrfToken.value = sessionCsrfToken;
      expiresIn.value = sessionExpiresIn;
      tokenIssuedAt = sessionAccessToken && sessionExpiresIn > 0 ? issuedAtMs || Date.now() : 0;
      // * Broadcast on local token acquisition (login/refresh) so sibling tabs adopt. When invoked
      //   from a TOKEN_UPDATE receive we must NOT re-broadcast — re-broadcasting from the receiver
      //   makes 2+ tabs ping-pong the same token forever (infinite loop).
      if (broadcast) {
        broadcastTokenUpdate(sessionAccessToken, sessionCsrfToken, sessionExpiresIn);
      }
      clearLegacyToken();
    };
    // * Whether the in-memory access token has exceeded its lifetime. A token with no known
    //   lifetime (issuedAt 0) is treated as not-yet-expired so we don't aggressively bounce.
    const isAccessTokenExpired = (): boolean =>
      isAuthenticated.value && tokenIssuedAt > 0 && expiresIn.value > 0
        ? Date.now() >= tokenIssuedAt + expiresIn.value * 1000
        : false;
    // * Focus-triggered expiry check: refresh if the access token has expired. Collapse ONLY on a
    //   definitive auth failure (401/403 from the refresh call itself) — a transient failure
    //   (429/5xx/network) must NOT log the user out; leave the (possibly still valid) session
    //   untouched and let the next focus retry.
    const refreshSessionIfExpired = async (): Promise<void> => {
      const decision = decideEnsureSession({
        adoptWindowActive: hasRecentTokenUpdate(),
        isExpired: isAccessTokenExpired(),
      });
      if (decision !== "refresh") {
        // * 'noop' (token still valid) or 'adopt' (a sibling refreshed within the window).
        return;
      }
      const result = await ensureSession(useIdentityStore());
      if (result?.accessToken) {
        return;
      }
      const failure = decideRefreshFailure({
        hasRecentTokenUpdate: false,
        adoptRetried: false,
        refreshFailedWith: result?.failedWith,
      });
      if (failure.action === "collapse") {
        // * The refresh itself was rejected as unauthorized — the session is truly gone.
        collapseSession();
        return;
      }
      logger.warn(
        "Transient refresh failure (status {status}); session left untouched — retrying on next focus.",
        { status: result?.failedWith ?? null },
      );
    };
    // * Re-arm expiry on tab focus (visibilitychange covers both the tab being hidden and shown).
    const handleFocusRefresh = (): void => {
      if (document.visibilityState === "visible") {
        void refreshSessionIfExpired();
      }
    };
    const clearLocalSessionState = (): void => {
      localStorage.removeItem("token");
      userName.value = "";
      userEmail.value = "";
      // * Keep the last-used accountId across a logout + re-login so the same user's next
      //   session (and freshly-booted tabs) land in the same account instead of resetting to
      //   masterAccountId. parseLoggedInUser already falls back to masterAccountId when the
      //   retained accountId isn't in the (re-sourced) account list — e.g. a different user
      //   logging in on the same browser, or the account being removed.
      roles.value = [];
      accounts.value = [];
      clearTokens();
      tokenIssuedAt = 0;
      // * Set ID last so data is available for any watchers.
      userId.value = 0;
    };
    // * Collapse to logged-out without a server round-trip. Used when the server has already told
    //   us the session is dead (401 on refresh/profile), so we must NOT call sessionLogout() (it
    //   would itself 401 and recurse). Broadcasts so sibling tabs log out too — BUT only when a
    //   session actually existed: if already logged out (re-entrant collapse, e.g. a sibling's
    //   dead-session logout() 401s back into this branch), skip the broadcast. Otherwise a LOG_OUT
    //   ping-pong loop forms across tabs (collapse → broadcast → sibling logout() 401 → collapse → …).
    const collapseSession = (): void => {
      const hadSession = accessToken.value.length > 0;
      clearLocalSessionState();
      if (hadSession) {
        broadcastLogout();
      }
    };
    const parseLoggedInUser = (user: LoggedInUser): void => {
      userName.value = displayUserName(user);
      userEmail.value = user.email;
      if ((user?.accounts ?? []).length > 0) {
        accounts.value = user.accounts.map((account) => ({
          id: account.id,
          name: account.name,
          ianaTimeZoneString: account.ianaTimeZoneString,
        }));
        // * Only initial-fill accountId from masterAccountId when no active account is
        //   selected yet (or the selected one is no longer accessible). Preserve any
        //   in-session account switch across rehydration so x-account-id always tracks
        //   the current active account, not the master.
        if (!userAccountIds.value.includes(accountId.value)) {
          accountId.value =
            (user?.masterAccountId ?? 0) > 0 ? user.masterAccountId : (accounts.value[0]?.id ?? 0);
        }
      }
      if ((user?.roles ?? []).length > 0) {
        roles.value = user.roles.map((role) => role.role);
      }
      // * Set ID last so data is available for any watchers.
      userId.value = user.id;
      // * Update Users List
      updateUserList();
    };
    const applySessionUser = (user: LoggedInUser, session: SessionAuthResponse | null): void => {
      if (session?.accessToken) {
        setSessionTokens(session.accessToken, session.csrfToken, session.expiresIn ?? 0);
      }
      parseLoggedInUser(user);
    };
    // * Boot rehydration: exchange the HttpOnly refresh cookie for fresh tokens, then recover the
    //   rich profile (their permissions may have changed since they last opened the app).
    const rehydrate = async (): Promise<boolean> => {
      const session = await ensureSession(useIdentityStore());
      if (!session?.accessToken) {
        return false;
      }
      // * A transient profile failure (429 rate-limit / 5xx / network) during a parallel-boot
      //   burst is NOT a dead session — the refresh succeeded and the token is valid. Retry
      //   briefly, and only collapse when the profile answers with a definitive auth failure
      //   (401/403). Collapsing on a transient error would wipe the persisted partial (user +
      //   retained accountId) and log the tab out on a legitimate rate-limit.
      let user: LoggedInUser | null = null;
      let profileErrors: EntityServiceResponseError[] = [];
      const maxAttempts = 3;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await profile();
        user = result.user;
        profileErrors = result.errors;
        if (user && (user?.id ?? 0) > 0) {
          setSessionTokens(session.accessToken, session.csrfToken, session.expiresIn ?? 0);
          parseLoggedInUser(user);
          return true;
        }
        if (!shouldRetryIdentityResolution({ errors: profileErrors, attempt, maxAttempts })) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      if (decideIdentityResolutionFailure({ errors: profileErrors }).action === "collapse") {
        collapseSession();
        return false;
      }
      // * Transient exhaustion (429/5xx/network): ensureSession already installed valid in-memory
      //   tokens, but the identity never resolved. Drop ONLY the in-memory tokens — no
      //   persisted-state wipe (userName + retained accountId stay), no LOG_OUT broadcast
      //   (sibling tabs are unaffected; their profile may have succeeded).
      clearSessionTokens();
      return false;
    };
    const login = async ({ email = "", password = "" } = {}): Promise<void> => {
      const { errors, session } = await sessionLogin({ email, password });
      const user = session?.user ?? null;
      if (user && (user?.id ?? 0) > 0 && session?.accessToken) {
        applySessionUser(user, session);
        // * Persist the server-minted device binding so refresh/profile calls carry x-device-id.
        storeDeviceId(session.deviceId);
        broadcastLogin(user);
      } else {
        useRequestErrorToast(errors, "Error Signing In");
      }
    };
    // * Quick login from a scanned user badge: the badge carries the user's id + checksum. Resolve
    //   the id → email (persisted user list first, else the public quickVerify endpoint), then call
    //   the v2 session quickLogin. The account list may be unavailable pre-login, so userList may
    //   return empty — fall back to quickVerify.
    const quickLogin = async ({
      userId: loginUserId = 0,
      pin = 0,
      checksum = "",
    } = {}): Promise<void> => {
      let email = "";
      try {
        const { users } = await userList(loginUserId, true);
        email = users?.[0]?.email || "";
      } catch {
        email = "";
      }
      if (!email) {
        const verifyResult = await quickVerify({ userId: loginUserId, checksum });
        const verified = verifyResult?.user;
        email = verified?.email || "";
        if (verified?.uuid && verified.uuid !== checksum) {
          checksum = verified.uuid;
        }
      }
      if (!email) {
        useRequestErrorToast(
          [{ message: "Unable to resolve login credentials.", code: null, reference: null }],
          "Error Signing In",
        );
        return;
      }
      const { errors, session } = await sessionQuickLogin({ email, pin, checksum });
      const user = session?.user ?? null;
      if (user && (user?.id ?? 0) > 0 && session?.accessToken) {
        applySessionUser(user, session);
        // * Persist the server-minted device binding so refresh/profile calls carry x-device-id.
        storeDeviceId(session.deviceId);
        broadcastLogin(user);
      } else {
        useRequestErrorToast(errors, "Error Signing In");
      }
    };
    const updateUserList = async (forceRefresh = false): Promise<void> => {
      const { errors, users } = await userList(userId.value, forceRefresh);
      // TODO: Do Errors need to be handled here?
      if (errors.length > 0) {
        logger.error("Failed to fetch user list: {errors}", { errors });
      }
      userDictionary.value = users.reduce<Record<PersistedUser["id"], PersistedUser>>(
        (acc, user) => {
          acc[user.id] = user;
          return acc;
        },
        {},
      );
    };
    const refreshUserList = async (): Promise<void> => {
      await updateUserList(true);
    };
    const logout = async ({ broadcast = true } = {}): Promise<void> => {
      clearLocalSessionState();
      try {
        await sessionLogout();
      } catch {
        // best-effort server-side clear; still reset local state
      }
      if (broadcast) {
        broadcastLogout();
      }
    };
    const hasPermission = (
      {
        requiredRoles = [],
        requiredGroups = [],
      }: {
        requiredRoles: (keyof (typeof PermissionControls)["roles"])[];
        requiredGroups: (keyof (typeof PermissionControls)["groups"])[];
      } = {
        requiredRoles: [],
        requiredGroups: [],
      },
    ): boolean => {
      if (requiredGroups.length === 0 && requiredRoles.length === 0) {
        return true;
      }
      for (const userRole of roles.value) {
        for (const group of requiredGroups) {
          const hasRole = (PermissionControls?.groups?.[group] ?? []).includes(userRole);
          if (hasRole) {
            return true;
          }
        }
        for (const role of requiredRoles) {
          const hasRole = (PermissionControls?.roles?.[role] ?? []).includes(userRole);
          if (hasRole) {
            return true;
          }
        }
      }
      return false;
    };
    const hasMasterAccess = computed(() => {
      return hasPermission({
        requiredRoles: [],
        requiredGroups: ["masterAccess"],
      });
    });
    const getUserById = (lookupUserId: number): PersistedUser => {
      return (
        userDictionary?.value[lookupUserId] ?? {
          id: 0,
          firstName: "Unknown",
          lastName: "User",
          email: "unknown@test.com",
        }
      );
    };

    return {
      userAccountIds,
      updateAccountId,
      accountId,
      userId,
      userName,
      userEmail,
      roles,
      userDictionary,
      getUserById,
      updateUserList,
      refreshUserList,
      accounts,
      accessToken,
      csrfToken,
      expiresIn,
      isAccessTokenExpired,
      refreshSessionIfExpired,
      handleFocusRefresh,
      setSessionTokens,
      clearSessionTokens,
      rehydrate,
      login,
      quickLogin,
      parseLoggedInUser,
      logout,
      collapseSession,
      noteBroadcastTokenUpdate,
      hasRecentTokenUpdate,
      isAuthenticated,
      hasPermission,
      returnUrl,
      activeAccount,
      hasMasterAccess,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ["userId", "userName", "userEmail", "accountId"],
    },
  },
);
